// ============================================================
// Craft a Brew — Mead Recipe Builder PDF & Print Export v1.0
// Date: April 21, 2026
// Load AFTER mead-builder.js
//
// Mirrors the beer recipe-pdf-export.js architecture exactly:
//   • Same IIFE pattern, same button injection, same modal
//   • Same html2canvas → jsPDF.addImage pipeline for PDF save
//   • Same print window flow for browser printing
//   • Layout adapted for mead-specific data:
//       1. HEADER: CAB logo + recipe name
//       2. STAT STRIP: OG / Primary FG / Final FG / ABV / Sweetness
//       3. BATCH INFO + tasting notes
//       4. EXPECTED SPECS table
//       5. HONEY BILL table
//       6. FRUIT / JUICE ADDITIONS table (melomel/cyser only)
//       7. SPICE / BOTANICAL ADDITIONS table (metheglin only)
//       8. YEAST & FERMENTATION table
//       9. NUTRIENT SCHEDULE table
//      10. FERMENTATION TIMELINE chart (captured from canvas)
//      11. BREW DAY NOTES (ruled lines)
//      12. FOOTER
// ============================================================

(function () {
  'use strict';

  // ============================================================
  // CONFIG
  // ============================================================
  var LOGO_ASSET_NAME = 'cab_circle_logoAsset 1.svg';
  var LOGO_URL = ((window.SRM_IMAGE_BASE || 'https://craftabrew.com/cdn/shop/t/38/assets/') + LOGO_ASSET_NAME).replace(/ /g, '%20');

  // ============================================================
  // 1. Wait for the mead calculator to be ready
  // ============================================================
  function ready(fn) {
    if (typeof recipe !== 'undefined' && typeof HONEYS !== 'undefined' && document.getElementById('fermChart')) {
      fn();
    } else {
      setTimeout(function () { ready(fn); }, 100);
    }
  }
  ready(init);

  function init() {
    try {
      injectStyles();
      injectFullSection();
      hideHardcodedExportButtons();
      console.log('[CAB] Mead PDF/Print export v1.0 loaded.');
    } catch (e) {
      console.error('[CAB] Mead PDF export failed to initialize:', e);
    }
  }

  // ============================================================
  // 1b. Hide any hardcoded export buttons in the sidebar/cart area
  // ============================================================
  function hideHardcodedExportButtons() {
    // Remove the .cart-export div that contains hardcoded SAVE PDF / PRINT buttons
    var cartExports = document.querySelectorAll('.cart-export');
    cartExports.forEach(function (el) { el.style.display = 'none'; });
    // Also hide any export buttons that match the old pattern
    document.querySelectorAll('.cart-export-btn').forEach(function (el) {
      if (el.closest('.cab-export-section')) return; // don't hide our own
      el.style.display = 'none';
    });
  }

  // ============================================================
  // 2. Styles for buttons, section, and modal
  // ============================================================
  function injectStyles() {
    if (document.getElementById('cab-mead-export-styles')) return;
    var css = [
      '.cab-mb .cab-export-section {',
      '  background: linear-gradient(135deg, #2e1a00 0%, #1a1000 100%) !important;',
      '  border-radius: 14px !important;',
      '  padding: 36px 36px 40px 36px !important;',
      '  margin: 28px 0 36px 0 !important;',
      '  text-align: center !important;',
      '  box-shadow: 0 4px 14px rgba(46,26,0,0.12) !important;',
      '  display: block !important;',
      '  box-sizing: border-box !important;',
      '}',
      '.cab-mb .cab-export-section h3 {',
      '  color: #ffffff !important;',
      '  font-size: 1.3em !important;',
      '  margin: 0 0 12px 0 !important;',
      '  padding: 0 !important;',
      '  font-weight: 700 !important;',
      '  letter-spacing: 0.5px !important;',
      '  font-family: inherit !important;',
      '}',
      '.cab-mb .cab-export-section p {',
      '  color: rgba(255,255,255,0.7) !important;',
      '  font-size: 0.92em !important;',
      '  margin: 0 auto 26px auto !important;',
      '  padding: 0 !important;',
      '  max-width: 520px !important;',
      '  line-height: 1.55 !important;',
      '}',
      '.cab-mb .cab-export-buttons {',
      '  display: flex !important;',
      '  gap: 14px !important;',
      '  justify-content: center !important;',
      '  flex-wrap: wrap !important;',
      '  margin: 0 !important;',
      '  padding: 0 !important;',
      '}',
      '.cab-mb .cab-export-btn {',
      '  border: none;',
      '  border-radius: 8px !important;',
      '  padding: 13px 28px !important;',
      '  font-size: 0.95em !important;',
      '  font-weight: 700 !important;',
      '  cursor: pointer;',
      '  display: inline-flex !important;',
      '  align-items: center !important;',
      '  gap: 9px !important;',
      '  transition: all 0.2s ease !important;',
      '  font-family: inherit !important;',
      '  letter-spacing: 0.3px !important;',
      '  margin: 0 !important;',
      '}',
      '.cab-mb .cab-export-btn-primary {',
      '  background: linear-gradient(135deg, #E8A832, #c4882a) !important;',
      '  color: #1a1a1a !important;',
      '}',
      '.cab-mb .cab-export-btn-primary:hover {',
      '  transform: translateY(-1px) !important;',
      '  box-shadow: 0 4px 14px rgba(232,168,50,0.4) !important;',
      '}',
      '.cab-mb .cab-export-btn-secondary {',
      '  background: transparent !important;',
      '  color: #ffffff !important;',
      '  border: 1.5px solid rgba(255,255,255,0.35) !important;',
      '}',
      '.cab-mb .cab-export-btn-secondary:hover {',
      '  background: rgba(255,255,255,0.07) !important;',
      '  border-color: rgba(255,255,255,0.6) !important;',
      '}',
      '.cab-mb .cab-export-btn svg {',
      '  width: 18px !important;',
      '  height: 18px !important;',
      '  stroke: currentColor !important;',
      '  stroke-width: 2.2 !important;',
      '  fill: none !important;',
      '  flex-shrink: 0 !important;',
      '}',
      '.cab-mb .cab-export-btn svg * {',
      '  fill: none !important;',
      '  stroke: currentColor !important;',
      '}',
      '.cab-mead-modal-overlay {',
      '  position: fixed; top: 0; left: 0; right: 0; bottom: 0;',
      '  background: rgba(46,26,0,0.65);',
      '  display: flex; align-items: center; justify-content: center;',
      '  z-index: 100000; padding: 20px;',
      '  animation: cabMeadFadeIn 0.18s ease;',
      '}',
      '@keyframes cabMeadFadeIn { from { opacity: 0; } to { opacity: 1; } }',
      '.cab-mead-modal {',
      '  background: #ffffff; border-radius: 14px;',
      '  max-width: 460px; width: 100%;',
      '  padding: 32px 32px 28px 32px;',
      '  box-shadow: 0 20px 60px rgba(0,0,0,0.3);',
      '  font-family: inherit;',
      '  animation: cabMeadSlideUp 0.22s ease;',
      '}',
      '@keyframes cabMeadSlideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }',
      '.cab-mead-modal-icon {',
      '  width: 52px; height: 52px;',
      '  background: linear-gradient(135deg, #E8A832, #c4882a);',
      '  border-radius: 50%;',
      '  display: flex; align-items: center; justify-content: center;',
      '  margin: 0 auto 16px auto;',
      '}',
      '.cab-mead-modal-icon svg {',
      '  width: 26px !important; height: 26px !important;',
      '  stroke: #ffffff !important; stroke-width: 2.2 !important; fill: none !important;',
      '}',
      '.cab-mead-modal h3 {',
      '  font-family: Kreon, serif;',
      '  font-size: 1.45rem;',
      '  margin: 0 0 8px 0;',
      '  text-align: center; color: #1a1a1a; font-weight: 400;',
      '}',
      '.cab-mead-modal p {',
      '  font-size: 0.92rem; color: #7a756d;',
      '  text-align: center; margin: 0 0 22px 0; line-height: 1.5;',
      '}',
      '.cab-mead-modal label {',
      '  display: block;',
      '  font-size: 0.78rem; text-transform: uppercase; letter-spacing: 0.5px;',
      '  color: #92400e; font-weight: 700; margin-bottom: 8px;',
      '}',
      '.cab-mead-modal input[type="text"] {',
      '  width: 100%; padding: 12px 16px;',
      '  border: 1.5px solid #e8e4dc; border-radius: 10px;',
      '  font-size: 1rem; font-family: inherit; color: #1a1a1a;',
      '  box-sizing: border-box; transition: border-color 0.15s ease;',
      '}',
      '.cab-mead-modal input[type="text"]:focus {',
      '  outline: none; border-color: #E8A832;',
      '}',
      '.cab-mead-modal-actions {',
      '  display: flex; gap: 10px; margin-top: 24px;',
      '}',
      '.cab-mead-modal-btn {',
      '  flex: 1; padding: 12px 20px;',
      '  border-radius: 8px; font-size: 0.92rem; font-weight: 700;',
      '  cursor: pointer; border: none;',
      '  transition: all 0.2s ease; font-family: inherit;',
      '  letter-spacing: 0.3px;',
      '}',
      '.cab-mead-modal-btn-cancel { background: #f5f3ee; color: #7a756d; }',
      '.cab-mead-modal-btn-cancel:hover { background: #e8e4dc; }',
      '.cab-mead-modal-btn-confirm {',
      '  background: linear-gradient(135deg, #E8A832, #c4882a);',
      '  color: #1a1a1a;',
      '}',
      '.cab-mead-modal-btn-confirm:hover {',
      '  transform: translateY(-1px);',
      '  box-shadow: 0 4px 12px rgba(232,168,50,0.4);',
      '}'
    ].join('\n');

    var style = document.createElement('style');
    style.id = 'cab-mead-export-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }

  // ============================================================
  // 3. Inject full section below the timeline card
  // ============================================================
  function injectFullSection() {
    // Look for the timeline card in the mead builder
    var timeline = document.querySelector('.timeline-card') || document.querySelector('[id*="timeline"]');
    if (!timeline || !timeline.parentNode) return;

    var section = document.createElement('div');
    section.className = 'cab-export-section';

    // Walk up to find the wrapper that has the mead builder class
    var wrapper = timeline.closest('.cab-mb') || timeline.parentNode;
    if (!wrapper.classList.contains('cab-mb')) {
      wrapper.classList.add('cab-mb');
    }

    section.innerHTML =
      '<h3>SAVE YOUR MEAD RECIPE</h3>' +
      '<p>Download a branded PDF for brew day reference, or print a copy for your meadmaking notebook. Includes your full honey bill, nutrient schedule, and fermentation timeline.</p>' +
      '<div class="cab-export-buttons">' +
      '<button type="button" class="cab-export-btn cab-export-btn-primary" data-action="pdf">' +
      svgIcon('download') + 'Save as PDF' +
      '</button>' +
      '<button type="button" class="cab-export-btn cab-export-btn-secondary" data-action="print">' +
      svgIcon('printer') + 'Print Recipe' +
      '</button>' +
      '</div>';

    timeline.parentNode.insertBefore(section, timeline.nextSibling);

    section.querySelector('[data-action="pdf"]').addEventListener('click', function () {
      promptForName(generatePDF);
    });
    section.querySelector('[data-action="print"]').addEventListener('click', function () {
      promptForName(printRecipe);
    });
  }


  // ============================================================
  // 4. SVG icon helper
  // ============================================================
  function svgIcon(name) {
    var icons = {
      download: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>',
      printer: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>',
      bookmark: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"/></svg>'
    };
    return icons[name] || '';
  }

  // ============================================================
  // 6. Custom modal for recipe name input
  // ============================================================
  function promptForName(callback) {
    var existing = document.querySelector('.cab-mead-modal-overlay');
    if (existing) existing.remove();

    // Generate a smart default name from the recipe
    var defaultName = generateDefaultName();

    var overlay = document.createElement('div');
    overlay.className = 'cab-mead-modal-overlay';
    overlay.innerHTML =
      '<div class="cab-mead-modal" role="dialog" aria-modal="true">' +
      '<div class="cab-mead-modal-icon">' + svgIcon('bookmark') + '</div>' +
      '<h3>Name Your Mead</h3>' +
      '<p>Give your mead a memorable name. This will appear on your saved PDF or printed recipe sheet.</p>' +
      '<label for="cab-mead-name-input">Recipe Name</label>' +
      '<input type="text" id="cab-mead-name-input" placeholder="e.g. Blackberry Bliss Melomel" maxlength="80" value="' + escapeHtml(defaultName) + '" />' +
      '<div class="cab-mead-modal-actions">' +
      '<button type="button" class="cab-mead-modal-btn cab-mead-modal-btn-cancel">Cancel</button>' +
      '<button type="button" class="cab-mead-modal-btn cab-mead-modal-btn-confirm">Continue</button>' +
      '</div>' +
      '</div>';

    document.body.appendChild(overlay);

    var input = overlay.querySelector('#cab-mead-name-input');
    setTimeout(function () { input.focus(); input.select(); }, 50);

    function close() { overlay.remove(); }
    function submit() {
      var name = input.value.trim() || 'My Custom Mead';
      close();
      callback(name);
    }

    overlay.querySelector('.cab-mead-modal-btn-cancel').addEventListener('click', close);
    overlay.querySelector('.cab-mead-modal-btn-confirm').addEventListener('click', submit);

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); submit(); }
      if (e.key === 'Escape') { close(); }
    });

    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) close();
    });
  }

  function generateDefaultName() {
    var type = recipe.type || 'traditional';
    var typeNames = { traditional: 'Traditional Mead', melomel: 'Melomel', cyser: 'Cyser', metheglin: 'Metheglin' };
    var baseName = typeNames[type] || 'Mead';

    // Add first fruit name for melomel
    if (type === 'melomel' && recipe.fruits.length > 0) {
      var fruit = FRUITS.find(function (x) { return x.id === recipe.fruits[0].fruitId; });
      if (fruit) baseName = fruit.name + ' ' + baseName;
    }
    // Add first spice for metheglin
    if (type === 'metheglin' && recipe.spices.length > 0) {
      var spice = SPICES.find(function (x) { return x.id === recipe.spices[0].spiceId; });
      if (spice) baseName = spice.name + ' ' + baseName;
    }
    return baseName;
  }

  // ============================================================
  // 7. Gather mead recipe data from the calculator state
  // ============================================================
  function gatherMeadData() {
    var data = {
      type: recipe.type || 'traditional',
      batchSize: recipe.batchSize || 1,
      targetABV: recipe.targetABV || 0,
      sweetness: recipe.sweetness || 0,
      backsweetFG: recipe.backsweetFG || 1.000,
      fermTemp: recipe.fermTemp || 68,
      nutrientProtocol: recipe.nutrientProtocol || 'cab',
      honeys: [],
      fruits: [],
      spices: [],
      yeast: null,
      stats: {
        og: textEl('sOG'),
        primaryFG: '1.000',
        finalFG: textEl('sFinalFG'),
        abv: textEl('sABV'),
        sweetness: textEl('sSweetness'),
        totalHoney: textEl('sHoney')
      },
      backsweetenHoney: '',
      tastingNotes: textEl('tastingText'),
      timeline: {
        og: textEl('tlOG'),
        fg: textEl('tlFG'),
        abv: textEl('tlABV'),
        estDays: textEl('tlDays')
      }
    };

    // Backsweeten info
    if (recipe.backsweetFG > 1.002) {
      data.backsweetenHoney = textEl('sBackHoney');
    }

    // Honeys
    recipe.honeys.forEach(function (h) {
      var honey = HONEYS.find(function (x) { return x.id === h.honeyId; });
      if (!honey) return;
      data.honeys.push({
        name: honey.name,
        brand: honey.brand || '',
        amount: h.amount.toFixed(1) + ' lb',
        ppg: honey.ppg,
        flavor: honey.flavor
      });
    });

    // Fruits
    recipe.fruits.forEach(function (f) {
      var fruit = FRUITS.find(function (x) { return x.id === f.fruitId; });
      if (!fruit) return;
      var weightLb = fruit.isJuice ? f.amount * (fruit.lbPerQt || 2.2) : f.amount;
      var sugarLb = weightLb * (fruit.sugar / 100);
      var gravPoints = (46 * sugarLb) / recipe.batchSize;
      data.fruits.push({
        name: fruit.name,
        amount: f.amount.toFixed(1) + ' ' + fruit.unit,
        sugar: fruit.sugar.toFixed(1) + '%',
        contribution: '+' + gravPoints.toFixed(0) + ' pts'
      });
    });

    // Spices
    recipe.spices.forEach(function (s) {
      var spice = SPICES.find(function (x) { return x.id === s.spiceId; });
      if (!spice) return;
      data.spices.push({
        name: spice.name,
        amount: s.amount + ' ' + spice.unit,
        note: spice.note
      });
    });

    // Yeast
    if (recipe.yeast) {
      var y = recipe.yeast;
      var packetsNeeded = Math.max(1, Math.ceil(recipe.batchSize / 5));
      data.yeast = {
        name: y.name,
        abvMax: y.abvMax + '% ABV',
        tempRange: y.tempMin + '\u2013' + y.tempMax + '\u00B0F',
        optimal: y.optimal + '\u00B0F',
        character: y.char,
        note: y.note,
        packets: packetsNeeded === 1 ? '1 packet' : packetsNeeded + ' packets',
        form: y.form || 'dry',
        packetWeight: y.packetWeight || '5g'
      };
    }

    // Nutrient schedule data
    data.nutrients = gatherNutrientData();

    if (data.tastingNotes === 'Add ingredients to generate a flavor profile...') data.tastingNotes = '';

    return data;
  }

  function gatherNutrientData() {
    // SINGLE SOURCE OF TRUTH: read the schedule the live calculator already
    // rendered into #nutrientSchedule rather than recomputing the doses here.
    // The PDF can no longer disagree with the on-screen amounts even if the
    // dosing formula changes later. (Fixes the June 2026 desync where the PDF
    // showed 3.7/2.3/2.3 against the live calculator's 2.6/1.6/1.6 because this
    // function carried an out-of-date copy of the scaling formula.)
    var sched = document.getElementById('nutrientSchedule');
    if (!sched) return null;

    var protocol = recipe.nutrientProtocol === 'cab'
      ? 'CAB Mead Yeast Nutrient'
      : 'TOSNA 3.0 (Fermaid-O)';

    var steps = [];
    sched.querySelectorAll('.nutrient-day').forEach(function (row) {
      var label   = row.querySelector('.day-label');
      var num     = row.querySelector('.day-n');
      var product = row.querySelector('.nutrient-day-product');
      var amount  = row.querySelector('.nutrient-day-amount');
      var note    = row.querySelector('.nutrient-day-note');
      steps.push({
        day: ((label ? label.textContent.trim() : '') + ' ' +
              (num ? num.textContent.trim() : '')).trim(),
        product: product ? product.textContent.trim() : '',
        amount: amount ? amount.textContent.trim() : '',
        note: note ? note.textContent.trim() : ''
      });
    });

    // Pull the gram total out of the summary line ("Total: 5.8g per batch.")
    // and reformat to the PDF's existing "X g total" convention so the rendered
    // line stays "<total> per batch".
    var total = '';
    var strongs = sched.querySelectorAll('strong');
    if (strongs.length) {
      var m = strongs[strongs.length - 1].textContent.match(/Total:\s*([\d.]+)\s*g/i);
      if (m) total = m[1] + ' g total';
    }

    return { protocol: protocol, steps: steps, total: total };
  }

  // ============================================================
  // 8. Capture the fermentation timeline chart as a PNG
  // ============================================================
  function captureTimelineChart() {
    return loadHtml2Canvas().then(function () {
      if (!window.html2canvas) return null;

      var chartCanvas = document.getElementById('fermChart');
      if (chartCanvas) {
        try {
          return Promise.resolve(chartCanvas.toDataURL('image/png'));
        } catch (e) {
          // Fall through to html2canvas
        }
      }

      // Fallback: capture the chart container
      var chartWrap = chartCanvas ? chartCanvas.parentElement : null;
      if (!chartWrap) return Promise.resolve(null);

      return window.html2canvas(chartWrap, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true
      }).then(function (canvas) {
        return canvas.toDataURL('image/png');
      }).catch(function (err) {
        console.warn('[CAB] Timeline chart capture failed:', err);
        return null;
      });
    });
  }

  // ============================================================
  // 9. Save as PDF — routes through printRecipe with asSavePdf
  // ============================================================
  function generatePDF(recipeName) {
    printRecipe(recipeName, true);
  }

  // ============================================================
  // 10. Print Recipe — HTML version (also used for PDF capture)
  // ============================================================
  function printRecipe(recipeName, asSavePdf) {
    captureTimelineChart().then(function (chartDataUrl) {
      var data = gatherMeadData();

      var h = [];
      h.push('<!DOCTYPE html><html><head><meta charset="UTF-8">');
      h.push('<title>' + escapeHtml(recipeName) + ' \u2014 Craft a Brew Mead Recipe</title>');
      h.push('<link href="https://fonts.googleapis.com/css2?family=Kreon:wght@400;500;600;700&family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">');
      h.push('<style>');
      h.push('@page { margin: 0; size: letter; }');
      h.push('* { box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }');
      h.push('html, body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }');
      h.push('body { font-family: "DM Sans", Helvetica, Arial, sans-serif; color: #1a1a1a; font-size: 9pt; line-height: 1.35; margin: 0; padding: 0.6in 1in 0.6in 1in; }');
      h.push('@media screen { body { background: #e8e4dc; padding: 40px 20px; } .page-inner { background: #ffffff; max-width: 8.5in; margin: 0 auto; padding: 0.6in 1in 0.6in 1in; box-shadow: 0 4px 20px rgba(0,0,0,0.08); min-height: 11in; } }');
      h.push('@media print { .page-inner { padding: 0; max-width: none; box-shadow: none; background: transparent; } body { padding: 0.6in 1in 0.6in 1in; background: transparent; } }');

      // Header styles
      h.push('.header-top { display: flex; align-items: center; gap: 14px; margin-bottom: 8px; }');
      h.push('.logo-circle { width: 72px; height: 72px; border-radius: 50%; background: #E8A832; flex-shrink: 0; display: flex; align-items: center; justify-content: center; color: #1a1a1a; font-family: "Kreon", serif; font-weight: 700; font-size: 9pt; text-align: center; line-height: 1.1; overflow: hidden; }');
      h.push('.logo-circle img { width: 100%; height: 100%; object-fit: contain; }');
      h.push('.recipe-title { flex: 1; font-family: "Kreon", serif; font-size: 24pt; font-weight: 700; margin: 0; color: #1a1a1a; line-height: 1.05; text-transform: uppercase; word-wrap: break-word; overflow-wrap: break-word; }');

      // Stat strip (5 cells: OG, Primary FG, Final FG, ABV, Sweetness)
      h.push('.stat-strip-row { display: flex; justify-content: center; margin-bottom: 8px; }');
      h.push('.header-stats { display: flex; height: 40px; width: 100%; }');
      h.push('.header-stat-box { flex: 1; padding: 3px 5px; font-size: 8pt; font-weight: 700; color: #1a1a1a; border-right: 1.5px solid #E8A832 !important; }');
      h.push('.header-stat-box:last-child { border-right: none !important; }');

      // Batch info and tasting
      h.push('.batch-info { font-size: 8pt; color: #7a756d !important; font-weight: 700; margin: 4px 0 2px 0; }');
      h.push('.mead-type-line { font-size: 8pt; color: #E8A832 !important; font-weight: 700; margin-bottom: 2px; }');
      h.push('.tasting { font-size: 7.5pt; color: #1a1a1a; line-height: 1.45; margin: 0 0 6px 0; }');

      // Section headers — honey gold
      h.push('h2 { font-family: "Kreon", serif; font-size: 11pt; color: #E8A832 !important; margin: 8px 0 3px 0; padding: 0 0 1px 0; border-bottom: 1.8px solid #E8A832 !important; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }');

      // Tables
      h.push('table { width: 100%; border-collapse: collapse; margin-bottom: 3px; font-size: 7.5pt; }');
      h.push('th { text-align: left; padding: 4px 8px; font-weight: 700; border-bottom: 1px solid #e0dcd4; color: #1a1a1a; font-size: 8pt; white-space: nowrap; }');
      h.push('td { padding: 4px 8px; color: #1a1a1a; }');
      h.push('tr:nth-child(even) td { background: #f5f3ee !important; }');
      h.push('tr:nth-child(odd) td { background: #ffffff !important; }');

      // Nutrient schedule
      h.push('.nutrient-row { display: flex; align-items: flex-start; gap: 10px; padding: 5px 8px; font-size: 7.5pt; border-bottom: 1px solid #f0ece4; }');
      h.push('.nutrient-row:nth-child(even) { background: #f5f3ee !important; }');
      h.push('.nutrient-day-label { font-weight: 700; color: #8B6FBF; min-width: 70px; flex-shrink: 0; font-size: 8pt; }');
      h.push('.nutrient-amount { font-weight: 700; min-width: 50px; flex-shrink: 0; }');
      h.push('.nutrient-note { color: #7a756d; flex: 1; }');
      h.push('.nutrient-total { font-size: 7.5pt; color: #7a756d; margin-top: 4px; padding: 4px 8px; font-weight: 700; }');
      h.push('.nutrient-total strong { color: #c4882a; }');

      // Yeast detail line
      h.push('.yeast-detail-line { font-size: 7pt; color: #7a756d; margin: 1px 0 4px 8px; }');

      // Backsweeten callout
      h.push('.backsweeten-note { font-size: 7.5pt; color: #92400e; background: #fdf6e3 !important; padding: 6px 10px; border-left: 3px solid #E8A832 !important; margin: 4px 0 6px 0; border-radius: 0 4px 4px 0; }');

      // Chart
      h.push('.timeline-chart { width: 100%; margin-top: 8px; text-align: center; }');
      h.push('.timeline-chart img { max-width: 100%; max-height: 190px; display: block; margin: 0 auto; }');
      h.push('.timeline-day-axis { display: flex; justify-content: space-between; padding: 2px 32px 0 32px; font-size: 6.5pt; color: #7a756d; font-weight: 500; }');

      // Notes
      h.push('.notes-lines { margin-top: 4px; }');
      h.push('.notes-line { border-bottom: 1.2px solid #E8A832 !important; height: 22px; }');

      // Footer
      h.push('.footer { margin-top: 10px; text-align: center; font-size: 6.5pt; color: #7a756d; padding-top: 6px; border-top: 0.5px solid #e0dcd4; }');

      h.push('</style></head><body>');
      h.push('<div class="page-inner">');

      // === HEADER ===
      h.push('<div class="header-top">');
      h.push('<div class="logo-circle">');
      h.push('<img src="' + LOGO_URL + '" onerror="this.style.display=\'none\'; this.parentNode.innerHTML=\'CRAFT<br>A BREW\';" alt="Craft a Brew"/>');
      h.push('</div>');
      h.push('<div class="recipe-title">' + escapeHtml(recipeName) + '</div>');
      h.push('</div>');

      // === STAT STRIP (handwritten recording boxes) ===
      h.push('<div class="stat-strip-row">');
      h.push('<div class="header-stats">');
      var statLabels = ['OG:', 'Primary FG:', 'Final FG:', 'ABV:', 'Sweetness:'];
      for (var i = 0; i < statLabels.length; i++) {
        h.push('<div class="header-stat-box">' + statLabels[i] + '</div>');
      }
      h.push('</div>');
      h.push('</div>');

      // === BATCH INFO ===
      var typeNames = { traditional: 'Traditional Mead', melomel: 'Melomel', cyser: 'Cyser', metheglin: 'Metheglin' };
      var batchParts = [];
      batchParts.push('Batch: ' + data.batchSize + ' gal');
      batchParts.push('Type: ' + (typeNames[data.type] || data.type));
      if (data.yeast) batchParts.push('Yeast: ' + data.yeast.name);
      h.push('<div class="batch-info">' + batchParts.join('  |  ') + '</div>');

      // Mead type highlight
      var sweetLabel = data.stats.sweetness || 'Dry';
      h.push('<div class="mead-type-line">' + sweetLabel + ' ' + (typeNames[data.type] || 'Mead') + '  \u2022  Target ' + data.stats.abv + ' ABV</div>');

      // Tasting notes
      if (data.tastingNotes) {
        h.push('<div class="tasting">' + escapeHtml(data.tastingNotes) + '</div>');
      }

      // === EXPECTED SPECS TABLE ===
      h.push('<h2>EXPECTED SPECS</h2>');
      h.push('<table>');
      h.push('<tr><th>Recipe</th><th>OG</th><th>Primary FG</th><th>Final FG</th><th>ABV</th><th>Sweetness</th></tr>');
      h.push('<tr>');
      h.push('<td><strong>' + escapeHtml(recipeName) + '</strong></td>');
      h.push('<td>' + escapeHtml(data.stats.og || '\u2014') + '</td>');
      h.push('<td>' + escapeHtml(data.stats.primaryFG || '1.000') + '</td>');
      h.push('<td>' + escapeHtml(data.stats.finalFG || '\u2014') + '</td>');
      h.push('<td>' + escapeHtml(data.stats.abv || '\u2014') + '</td>');
      h.push('<td>' + escapeHtml(sweetLabel) + '</td>');
      h.push('</tr>');
      h.push('</table>');

      // === HONEY BILL ===
      if (data.honeys.length) {
        h.push('<h2>HONEY BILL</h2>');
        h.push('<table>');
        h.push('<tr><th>Honey</th><th>Brand</th><th>Amount</th><th>PPG</th><th>Flavor Profile</th></tr>');
        data.honeys.forEach(function (hon) {
          h.push('<tr>');
          h.push('<td><strong>' + escapeHtml(hon.name) + '</strong></td>');
          h.push('<td>' + escapeHtml(hon.brand) + '</td>');
          h.push('<td>' + escapeHtml(hon.amount) + '</td>');
          h.push('<td>' + hon.ppg + '</td>');
          h.push('<td style="font-size:7pt;color:#7a756d;">' + escapeHtml(hon.flavor) + '</td>');
          h.push('</tr>');
        });
        h.push('</table>');
        // Total honey
        h.push('<div class="yeast-detail-line">Total honey: ' + escapeHtml(data.stats.totalHoney) + '</div>');
      }

      // === FRUIT / JUICE ADDITIONS ===
      if (data.fruits.length) {
        h.push('<h2>FRUIT &amp; JUICE ADDITIONS</h2>');
        h.push('<table>');
        h.push('<tr><th>Ingredient</th><th>Amount</th><th>Sugar %</th><th>Gravity Contribution</th></tr>');
        data.fruits.forEach(function (fr) {
          h.push('<tr>');
          h.push('<td>' + escapeHtml(fr.name) + '</td>');
          h.push('<td>' + escapeHtml(fr.amount) + '</td>');
          h.push('<td>' + escapeHtml(fr.sugar) + '</td>');
          h.push('<td>' + escapeHtml(fr.contribution) + '</td>');
          h.push('</tr>');
        });
        h.push('</table>');
      }

      // === SPICE / BOTANICAL ADDITIONS ===
      if (data.spices.length) {
        h.push('<h2>SPICE &amp; BOTANICAL ADDITIONS</h2>');
        h.push('<table>');
        h.push('<tr><th>Ingredient</th><th>Amount</th><th>Usage Notes</th></tr>');
        data.spices.forEach(function (sp) {
          h.push('<tr>');
          h.push('<td>' + escapeHtml(sp.name) + '</td>');
          h.push('<td>' + escapeHtml(sp.amount) + '</td>');
          h.push('<td style="font-size:7pt;color:#7a756d;">' + escapeHtml(sp.note) + '</td>');
          h.push('</tr>');
        });
        h.push('</table>');
      }

      // === YEAST & FERMENTATION ===
      if (data.yeast) {
        h.push('<h2>YEAST &amp; FERMENTATION</h2>');
        h.push('<table style="table-layout: fixed;">');
        h.push('<colgroup>');
        h.push('<col style="width: auto;"/>');
        h.push('<col style="width: 68px;"/>');
        h.push('<col style="width: 78px;"/>');
        h.push('<col style="width: 56px;"/>');
        h.push('<col style="width: 70px;"/>');
        h.push('<col style="width: 56px;"/>');
        h.push('</colgroup>');
        h.push('<tr><th>Strain</th><th>ABV Tol.</th><th>Temp Range</th><th>Ferm \u00B0F</th><th>Character</th><th>Packets</th></tr>');
        h.push('<tr>');
        h.push('<td>' + escapeHtml(data.yeast.name) + '</td>');
        h.push('<td>' + escapeHtml(data.yeast.abvMax) + '</td>');
        h.push('<td>' + escapeHtml(data.yeast.tempRange) + '</td>');
        h.push('<td>' + escapeHtml(data.fermTemp + '\u00B0F') + '</td>');
        h.push('<td style="font-size:7pt;">' + escapeHtml(data.yeast.character) + '</td>');
        h.push('<td>' + escapeHtml(data.yeast.packets) + '</td>');
        h.push('</tr>');
        h.push('</table>');

        // Yeast detail line
        var yd = [];
        if (data.yeast.packetWeight) yd.push(data.yeast.packetWeight + ' ' + data.yeast.form + ' packet');
        if (data.yeast.optimal) yd.push('Optimal: ' + data.yeast.optimal);
        if (data.yeast.note) yd.push(data.yeast.note);
        if (yd.length) {
          h.push('<div class="yeast-detail-line">' + escapeHtml(yd.join('  |  ')) + '</div>');
        }
      }

      // === NUTRIENT SCHEDULE ===
      if (data.nutrients) {
        h.push('<h2>NUTRIENT SCHEDULE (' + escapeHtml(data.nutrients.protocol) + ')</h2>');
        data.nutrients.steps.forEach(function (step, idx) {
          h.push('<div class="nutrient-row">');
          h.push('<span class="nutrient-day-label">' + escapeHtml(step.day) + '</span>');
          h.push('<span class="nutrient-amount">' + escapeHtml(step.amount) + '</span>');
          h.push('<span class="nutrient-note">' + escapeHtml(step.note) + '</span>');
          h.push('</div>');
        });
        h.push('<div class="nutrient-total"><strong>' + escapeHtml(data.nutrients.total) + '</strong> per batch</div>');
      }

      // === BACKSWEETENING NOTE ===
      if (data.backsweetFG > 1.002) {
        h.push('<div class="backsweeten-note">');
        h.push('<strong>Backsweetening:</strong> After fermentation completes and you stabilize with potassium sorbate + campden tablets, ');
        h.push('add approximately <strong>' + escapeHtml(data.backsweetenHoney) + '</strong> of honey to reach your target Final FG of <strong>' + data.backsweetFG.toFixed(3) + '</strong> (' + escapeHtml(sweetLabel) + ').');
        h.push('</div>');
      }

      // === FERMENTATION TIMELINE CHART ===
      if (chartDataUrl) {
        h.push('<h2>EXPECTED FERMENTATION TIMELINE</h2>');
        h.push('<div class="timeline-chart"><img src="' + chartDataUrl + '" alt="Fermentation timeline chart"/></div>');

        // Day axis labels matching the canvas chart
        var days = recipe.timelineDays || 30;
        var marks = [0, Math.round(days / 4), Math.round(days / 2), Math.round(3 * days / 4), days];
        h.push('<div class="timeline-day-axis">');
        marks.forEach(function (d) { h.push('<span>Day ' + d + '</span>'); });
        h.push('</div>');

        // Timeline stats below chart
        var tlParts = [];
        if (data.timeline.og && data.timeline.og !== '1.000') tlParts.push('Start SG: ' + data.timeline.og);
        if (data.timeline.fg) tlParts.push('Target FG: ' + data.timeline.fg);
        if (data.timeline.abv) tlParts.push('Est ABV: ' + data.timeline.abv);
        if (data.timeline.estDays) tlParts.push('Est Days: ' + data.timeline.estDays);
        if (tlParts.length) {
          h.push('<div class="yeast-detail-line" style="margin-top:4px;">' + escapeHtml(tlParts.join('  |  ')) + '</div>');
        }
      }

      // === BREW DAY NOTES ===
      h.push('<h2>BREW DAY NOTES</h2>');
      h.push('<div class="notes-lines">');
      for (var n = 0; n < 5; n++) h.push('<div class="notes-line"></div>');
      h.push('</div>');

      // === FOOTER ===
      h.push('<div class="footer">Generated by the Craft a Brew Mead Recipe Builder  \u2022  craftabrew.com</div>');

      h.push('</div>'); // .page-inner
      h.push('</body></html>');

      var fullHtml = h.join('\n');

      if (asSavePdf) {
        saveHtmlAsPdf(fullHtml, recipeName);
        return;
      }

      var printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('Pop-up blocked. Please allow pop-ups for craftabrew.com to print your mead recipe.');
        return;
      }
      printWindow.document.write(fullHtml);
      printWindow.document.close();
      setTimeout(function () {
        try { printWindow.print(); } catch (e) {}
      }, 600);
    });
  }

  // ============================================================
  // 11. saveHtmlAsPdf — render HTML in iframe, capture, build PDF
  // ============================================================
  function saveHtmlAsPdf(htmlString, recipeName) {
    loadJsPDF().then(function () {
      return loadHtml2Canvas();
    }).then(function () {
      if (!window.html2canvas) {
        throw new Error('html2canvas not available');
      }

      var iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;top:-10000px;left:-10000px;width:816px;height:1056px;border:none;opacity:0;pointer-events:none;';
      document.body.appendChild(iframe);

      function cleanup() {
        try { document.body.removeChild(iframe); } catch (e) {}
      }

      return new Promise(function (resolve, reject) {
        setTimeout(function () {
          try {
            var iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
            iframeDoc.open();
            iframeDoc.write(htmlString);
            iframeDoc.close();

            // Wait for fonts and images
            var fontsReady = iframeDoc.fonts && iframeDoc.fonts.ready
              ? iframeDoc.fonts.ready
              : Promise.resolve();

            var imagesReady = Promise.all(
              Array.prototype.map.call(iframeDoc.images || [], function (img) {
                if (img.complete && img.naturalWidth > 0) return Promise.resolve();
                return new Promise(function (res) {
                  var done = false;
                  function finish() { if (!done) { done = true; res(); } }
                  img.addEventListener('load', finish);
                  img.addEventListener('error', finish);
                  setTimeout(finish, 3000);
                });
              })
            );

            Promise.all([fontsReady, imagesReady]).then(function () {
              setTimeout(function () {
                captureAndBuildPdf(iframeDoc, recipeName)
                  .then(function () { cleanup(); resolve(); })
                  .catch(function (err) { cleanup(); reject(err); });
              }, 100);
            }).catch(function (err) {
              cleanup();
              reject(err);
            });
          } catch (e) {
            cleanup();
            reject(e);
          }
        }, 250);
      });
    }).catch(function (err) {
      if (!err.alreadyAlerted) {
        console.error('[CAB] Mead PDF generation failed:', err);
        alert('PDF generation failed. Please try printing instead.');
      }
    });
  }

  function captureAndBuildPdf(iframeDoc, recipeName) {
    var body = iframeDoc.body;
    var captureWidth = body.scrollWidth || 816;
    var captureHeight = body.scrollHeight || 1056;

    return window.html2canvas(body, {
      scale: 2,
      backgroundColor: '#ffffff',
      useCORS: true,
      logging: false,
      width: captureWidth,
      height: captureHeight,
      windowWidth: captureWidth,
      windowHeight: captureHeight
    }).then(function (canvas) {
      var jsPDF = window.jspdf.jsPDF;
      var pdf = new jsPDF({ orientation: 'portrait', unit: 'in', format: 'letter' });

      var pageWidth = 8.5;
      var pageHeight = 11;

      var totalImgHeight = canvas.height * (pageWidth / canvas.width);
      if (totalImgHeight <= pageHeight + 0.05) {
        pdf.addImage(
          canvas.toDataURL('image/png'),
          'PNG',
          0, 0,
          pageWidth, totalImgHeight
        );
      } else {
        var sliceHeightPx = Math.floor(canvas.width * (pageHeight / pageWidth));
        var yOffset = 0;
        var pageNum = 0;

        while (yOffset < canvas.height) {
          if (pageNum > 0) pdf.addPage();

          var thisSliceHeight = Math.min(sliceHeightPx, canvas.height - yOffset);

          var sliceCanvas = document.createElement('canvas');
          sliceCanvas.width = canvas.width;
          sliceCanvas.height = thisSliceHeight;
          var ctx = sliceCanvas.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
          ctx.drawImage(canvas, 0, -yOffset);

          var sliceData = sliceCanvas.toDataURL('image/png');
          var sliceHeightInches = (thisSliceHeight / canvas.width) * pageWidth;
          pdf.addImage(sliceData, 'PNG', 0, 0, pageWidth, sliceHeightInches);

          yOffset += sliceHeightPx;
          pageNum++;
        }
      }

      var safeFileName = recipeName
        .replace(/[^a-zA-Z0-9 ]/g, '')
        .replace(/\s+/g, '-')
        .toLowerCase();
      pdf.save(safeFileName + '-mead-recipe.pdf');
    });
  }

  // ============================================================
  // 12. Load jsPDF + html2canvas on demand
  // ============================================================
  function loadJsPDF() {
    if (window.jspdf && window.jspdf.jsPDF) {
      return Promise.resolve();
    }

    var jsPdfUrls = [
      'https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js',
      'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js'
    ];

    return loadScriptWithFallback(jsPdfUrls)
      .then(function () {
        if (!window.jspdf || !window.jspdf.jsPDF) {
          throw new Error('jsPDF did not register on window after script load');
        }
        return true;
      })
      .catch(function (err) {
        console.error('[CAB] Failed to load jsPDF:', err);
        alert('Could not load PDF libraries. Please check your internet connection and try again.');
        var libErr = new Error('LIBRARY_LOAD_FAILED');
        libErr.alreadyAlerted = true;
        throw libErr;
      });
  }

  function loadHtml2Canvas() {
    if (window.html2canvas) return Promise.resolve();
    return loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js')
      .catch(function (err) {
        console.warn('[CAB] html2canvas failed to load:', err);
        return null;
      });
  }

  function loadScriptWithFallback(urls) {
    var idx = 0;
    function tryNext() {
      if (idx >= urls.length) {
        return Promise.reject(new Error('All script URLs failed'));
      }
      var url = urls[idx++];
      return loadScript(url).catch(function (err) {
        console.warn('[CAB] Failed to load ' + url + ', trying next fallback...');
        return tryNext();
      });
    }
    return tryNext();
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  // ============================================================
  // 13. Utilities
  // ============================================================
  function textEl(id) {
    var el = document.getElementById(id);
    return el ? el.textContent.trim() : '';
  }

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
})();